import { GREETING_DESKTOP, vipHeading } from '@/constants/content'
import SearchFormProvider from '@/context/SearchFormContext'
import useBootstrapData from '@/hooks/useBootstrapData'
import useCityNameInFormHeadlineForSEM from '@/hooks/useCityNameInFormHeadlineForSEM'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useSEMSearchHeadline from '@/hooks/useSEMSearchHeadline'
import type { LOCATION_SEARCH_TYPE, ProductType } from '@/types'
import { Heading, Tabs } from '@pcln/horizon'
import { useEffect, useMemo, useState } from 'react'
import { PRODUCT_TABS_EXPERIMENT, tabIconUrls } from '../SearchForm/constants'
import useHeadlineValues from '../SearchForm/customHooks/useHeadlineValues'
import getProductCategory from './utils/getProductCategory'

export default function SearchFormNewHorizonTabs({
  currentTab,
  onFormChange,
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const [location, setLocation] = useState<LOCATION_SEARCH_TYPE | undefined>()
  const [activeTab, setActiveTab] = useState(currentTab)

  const {
    customer: { firstName: firstNameBootstrap },
    signInStatus: { signedIn: serverSideSignedIn },
  } = useBootstrapData()

  const { firstName: clientSideFirstName, signedIn: clientSideSignedIn } = useClientSideSignedInData()

  const getFirstName = () => {
    if (serverSideSignedIn) return firstNameBootstrap
    if (clientSideSignedIn) return clientSideFirstName
    return undefined
  }
  
  const firstName = getFirstName()

  const prePopHeadline = useCityNameInFormHeadlineForSEM(location)
  const SEMHeadline = useSEMSearchHeadline()
  const headlineValues = useHeadlineValues(currentTab)

  const headlineWithName = firstName ? vipHeading.headlineWithName(firstName) : GREETING_DESKTOP
  const defaultHeadline = firstName ? headlineWithName : headlineValues.hotels.headline
  const hotelHeadline = prePopHeadline || SEMHeadline || defaultHeadline
  const updatedHeadlineValues = useMemo(
    () => ({
      ...headlineValues,
      hotels: {
        ...headlineValues.hotels,
        headline: hotelHeadline,
      },
    }),
    [hotelHeadline, headlineValues],
  )

  const headline = updatedHeadlineValues[activeTab]?.headline ?? ''

  useEffect(() => {
    setActiveTab(currentTab)
  }, [currentTab])

  const handleOnTabClick = (productType: ProductType) => {
    if (productType === activeTab) {
      return
    }
    setActiveTab(productType)
    onFormChange(productType)
  }

  const tabsContent = useMemo(
    () =>
      PRODUCT_TABS_EXPERIMENT.map((productTab) => {
        const { component, bumper } = getProductCategory(
          productTab.id,
          updatedHeadlineValues,
          setLocation,
          true,
        )
        return {
          ...productTab,
          iconLeft: ({ className }: { className?: string }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img alt={productTab.id} src={tabIconUrls[productTab.id]} className={className} />
          ),
          panelContent: (
            <SearchFormProvider>
              <div className='flex flex-col'>
                <div className='p-3 md:p-4'>{component}</div>
                <div className='mt-4 w-full'>{bumper}</div>
              </div>
            </SearchFormProvider>
          ),
          value: productTab.id,
          label: productTab.tabText,
        }
      }),
    [updatedHeadlineValues],
  )

  const slotClassNames = useMemo(
    () => ({
      tabClass: '@max-3xl:flex-row @max-3xl:px-3 @max-3xl:gap-2',
      rootClass: 'overflow-visible',
      listClass: 'flex gap-1 md:gap-3 justify-start',
      panelClass: 'w-full max-w-7xl mt-4 bg-primary-1 md:rounded-xl md:shadow-lg overflow-hidden',
    }),
    [],
  )

  return (
    <div className='w-full'>
      <Heading
        as='h1'
        textStyle='heading2'
        palette='primary'
        className='text-heading2 max-md:text-heading3 md:text-heading2 min-[90rem]:pr-[130px] min-[90rem]:mx-0 min-[90rem]:text-heading1 m-0 mx-4 pb-4 px-0 text-left'
      >
        {headline}
      </Heading>
      <div className='w-auto'>
        <div className='mx-auto flex max-w-7xl flex-col justify-center gap-4 p-4'>
          <div className='flex w-full justify-center px-3 pt-3 md:px-0 md:pt-0'>
            <Tabs
              stacked
              tabsContent={tabsContent}
              value={activeTab}
              onValueChange={handleOnTabClick}
              slotClassNames={slotClassNames}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
